import { styled } from '@linaria/react';
import { ActionIcon, Collapse, Divider, Group, Stack } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProcessTask } from '../../../api/process-client/process-client.type';
import type { Task } from '../../../api/tasks-client/task-client.type';
import { useTaskSearch } from '../../../api/tasks-client/tasks-client.hooks';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../helpers';
import { resolveTaskTypeDisplayText } from '../../../helpers/task-type-labels/task-type-labels';
import { useTaskSearchHistory } from '../../../store/process-store/process-store.hooks';
import { baseWhite, graphButterscotch, inkLighter, secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { TaskSearchCombobox } from '../filters/task-search-combobox';
import { TaskMetadataList } from '../../work-periods/task-detail/task-metadata-list';

type Props = {
  task?: ProcessTask;
  onClose: () => void;
};

/**
 * Side panel of the unified process graph: the organization-wide Task search
 * that opens a task's graph, and the details of the task on screen.
 *
 * The search reads and writes the active portfolio's recent picks through
 * `useTaskSearchHistory`, so the list is the same one every Task search on the
 * Process page shows. Only a pick records a task; landing on a graph by link
 * does not. The field empties after a pick because the picked task is shown in
 * the panel itself, and picking the task already on screen keeps the URL, so an
 * open session or comment panel is not closed by a no-op pick.
 *
 * @param {Props} props - The task whose graph is on screen, if any, and the close handler
 * @returns {JSX.Element} The panel
 */
export const UnifiedProcessGraphSidePanel = ({ task, onClose }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [isInitiativesOpen, { toggle: toggleInitiativesOpen }] = useDisclosure(true);
  const { entries, addEntry, removeEntry, clearEntries } = useTaskSearchHistory();

  const { tasks } = useTaskSearch(debouncedSearch, null, null, null, null, null, null, {
    enabled: Boolean(debouncedSearch),
    queryKey: ['task-search', 'unified-process-graph', debouncedSearch],
  });

  const handlePick = useCallback(
    (picked: Task) => {
      addEntry(picked);
      if (picked.id === task?.id) {
        return;
      }
      navigate(`/application/process/unified/${picked.id}`);
    },
    [addEntry, navigate, task?.id],
  );

  const handleRemoveRecent = useCallback((recent: Task) => removeEntry(recent.id), [removeEntry]);

  const taskString = i18n.t('common.work_unit');

  const metaData = task
    ? [
        { label: i18n.t('common.created'), value: formatDate(new Date(task.created_date)) },
        {
          label: i18n.t('common.type'),
          value: resolveTaskTypeDisplayText(task) || '-',
        },
        { label: i18n.t('common.estimation'), value: `${task.points ?? '-'}` },
        { label: i18n.t('common.parent_task'), value: task.epic?.name || '-' },
      ]
    : [];

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap={5} align="center">
          <Text weight="bold" size="medium">
            {taskString} Search
          </Text>
          <Tooltip
            label={`Search for a ${taskString}. Type in the ${taskString.toLocaleLowerCase()} name or URL. Limit of 10 results.`}
            variant="black"
            multiline
            maw={300}
            zIndex={1000}
          >
            <Icon name="info" size={16} color={secondaryBase} />
          </Tooltip>
        </Group>
        <ActionIcon variant="subtle" color={secondaryBase} aria-label="Close panel" onClick={onClose}>
          <Icon name="close" size={18} color={secondaryBase} style={{ cursor: 'pointer' }} />
        </ActionIcon>
      </Group>

      <Group gap={5}>
        <TaskSearchCombobox
          style={{ flex: 1 }}
          selectedLabel=""
          selectedTaskId={task?.id ?? null}
          results={tasks}
          recentEntries={entries}
          onSearchChange={setSearchValue}
          onPick={handlePick}
          onRemoveRecent={handleRemoveRecent}
          onClearRecent={clearEntries}
          clearOnPick
          fieldName={`${taskString} search`}
          placeholder={`Type to search for a ${taskString}`}
          listName={`${taskString} suggestions`}
        />
      </Group>

      {task && (
        <>
          <Divider />
          <Group gap={5} align="center">
            <Text color={secondaryBase} size="medium" weight="bold">
              {task.title}
            </Text>
            {task.url && (
              <ActionIcon
                variant="subtle"
                color={secondaryBase}
                aria-label="Open task in new tab"
                onClick={() => window.open(task.url, '_blank', 'noopener,noreferrer')}
              >
                <Icon name="open_in_new" size={18} color={secondaryBase} style={{ cursor: 'pointer' }} />
              </ActionIcon>
            )}
          </Group>
          <Text size="medium" weight="bold">
            {task.name}
          </Text>
          <Divider />
          <TaskMetadataList metadata={metaData} textColor={inkLighter} />

          {task.initiatives && task.initiatives.length > 0 && (
            <>
              <Divider />
              <Group align="center" justify="space-between">
                <Text weight="bold">{i18n.t('common.initiatives')}</Text>
                <ActionIcon
                  variant="subtle"
                  color={secondaryBase}
                  aria-label="Toggle initiatives"
                  onClick={toggleInitiativesOpen}
                >
                  <Icon
                    name={isInitiativesOpen ? 'unfold_less' : 'unfold_more'}
                    size={16}
                    color={secondaryBase}
                    style={{ cursor: 'pointer' }}
                  />
                </ActionIcon>
              </Group>
              <Collapse expanded={isInitiativesOpen}>
                <Stack gap={2}>
                  {task.initiatives.map((initiative) => (
                    <RelatedObjectContainer key={initiative.id} color={graphButterscotch}>
                      <Group align="center" gap={4}>
                        <Icon name="ads_click" color={baseWhite} size={16} />
                        <Text size="small" color={baseWhite}>
                          {initiative.name}
                        </Text>
                      </Group>
                    </RelatedObjectContainer>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

const RelatedObjectContainer = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  padding: 2px 8px;
  border-radius: 8px;
  display: inline-flex;
  width: fit-content;
`;
